import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, Trophy, Target, Users, Flame, Zap, ShieldCheck, Award } from "lucide-react";
import { toast } from "sonner";
import { useSiteContent } from "@/hooks/useSiteContent";
import { DEFAULTS } from "@/lib/content_defaults";

const ICONS = { Flame, Trophy, Target, Users, Zap, ShieldCheck, Award };

export default function Home() {
  const { data: hero } = useSiteContent("home_hero", DEFAULTS.home_hero);
  const { data: mission } = useSiteContent("home_mission", DEFAULTS.home_mission);
  const { data: stats } = useSiteContent("home_stats", DEFAULTS.home_stats);
  const { data: features } = useSiteContent("home_features", DEFAULTS.home_features);
  const { data: cta } = useSiteContent("home_cta", DEFAULTS.home_cta);

  const [programs, setPrograms] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [email, setEmail] = useState("");

  useEffect(() => {
    api.get("/programs").then((r) => setPrograms(r.data));
    api.get("/testimonials").then((r) => setTestimonials(r.data));
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter", { email });
      toast.success("You're in. Check your inbox for training drops.");
      setEmail("");
    } catch { toast.error("Could not subscribe. Try again."); }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative section-dark min-h-[92vh] flex items-center overflow-hidden">
        <img src={hero.hero_image} alt="Lineman training" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 hero-bottom-fade" />
        <div className="container-app relative z-10 py-24">
          <div className="max-w-3xl fade-up">
            <div className="label-tag mb-6">{hero.label_tag}</div>
            <h1 className="font-heading font-black uppercase leading-[0.9] text-5xl sm:text-6xl md:text-7xl lg:text-8xl">
              <span className="text-white">{hero.line1}</span><br />
              <span className="metallic-gold">{hero.line2}</span><br />
              <span className="metallic-silver">{hero.line3}</span>
            </h1>
            <p className="mt-8 text-white/75 text-lg md:text-xl max-w-2xl leading-relaxed">{hero.subtitle}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to={hero.cta_primary_link} className="btn-gold" data-testid="hero-cta-join">{hero.cta_primary_text} <ArrowRight size={16} /></Link>
              <Link to={hero.cta_secondary_link} className="btn-ghost" data-testid="hero-cta-coaches">{hero.cta_secondary_text}</Link>
              <Link to={hero.cta_tertiary_link} className="btn-ghost" data-testid="hero-cta-programs">{hero.cta_tertiary_text}</Link>
            </div>
            <div className="mt-16 grid grid-cols-3 gap-8 max-w-lg">
              {(stats.items || []).slice(0, 3).map((s, i) => <StatBlock key={i} number={s.number} label={s.label} />)}
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="section-light py-24">
        <div className="container-app grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-4">
            <div className="label-tag mb-4" style={{ color: "#000" }}>{mission.label_tag}</div>
            <h2 className="font-heading font-black uppercase text-4xl md:text-5xl leading-none">{mission.title}</h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 space-y-6 text-lg leading-relaxed text-neutral-700">
            <p>{mission.paragraph_1}</p>
            <p>{mission.paragraph_2}</p>
            <div className="pt-2 flex gap-4 flex-wrap">
              <Link to={mission.cta1_link} className="btn-ghost-dark" data-testid="mission-meet-coaches">{mission.cta1_text}</Link>
              <Link to={mission.cta2_link} className="btn-ghost-dark" data-testid="mission-recruiting">{mission.cta2_text}</Link>
            </div>
          </div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="section-dark py-24 relative overflow-hidden">
        <div className="container-app relative z-10">
          <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
            <div>
              <div className="label-tag mb-4">Programs</div>
              <h2 className="font-heading font-black uppercase text-4xl md:text-5xl">Train Every Discipline.<br /><span className="metallic-gold">Master The Game.</span></h2>
            </div>
            <Link to="/programs" className="btn-ghost" data-testid="programs-view-all">View All Programs <ArrowRight size={16} /></Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {programs.slice(0, 6).map((p) => (
              <Link key={p.key} to="/programs" className="card-dark hover-lift group block relative overflow-hidden" data-testid={`program-card-${p.key}`}>
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500" />
                </div>
                <div className="p-6">
                  <div className="label-tag mb-2">{p.tagline}</div>
                  <div className="font-heading font-black uppercase text-2xl">{p.title}</div>
                  <p className="mt-3 text-white/60 text-sm leading-relaxed">{p.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="section-surface py-24">
        <div className="container-app">
          <div className="label-tag mb-4" style={{ color: "#000" }}>{features.label_tag}</div>
          <h2 className="font-heading font-black uppercase text-4xl md:text-5xl mb-12">{features.title}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {(features.items || []).map((f, i) => {
              const Icon = ICONS[f.icon] || Flame;
              return <Feature key={i} icon={<Icon size={28} />} title={f.title} text={f.text} />;
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-dark py-24">
        <div className="container-app">
          <div className="label-tag mb-4">Voices</div>
          <h2 className="font-heading font-black uppercase text-4xl md:text-5xl mb-14">Straight from the athletes.</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {testimonials.map((t, i) => (
              <div key={i} className="card-dark p-8" data-testid={`testimonial-${i}`}>
                <div className="text-[color:var(--gold)] text-4xl font-heading leading-none">&ldquo;</div>
                <p className="text-white/85 mt-2 text-lg leading-relaxed">{t.quote}</p>
                <div className="mt-6 pt-6 border-t border-white/10">
                  <div className="font-heading uppercase font-bold">{t.name}</div>
                  <div className="text-white/50 text-sm">{t.role} · {t.school}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + NEWSLETTER */}
      <section className="section-light py-24">
        <div className="container-app grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="label-tag mb-4" style={{ color: "#000" }}>{cta.label_tag}</div>
            <h2 className="font-heading font-black uppercase text-4xl md:text-6xl leading-none">{cta.title}</h2>
            <p className="mt-6 text-neutral-700 text-lg">{cta.subtitle}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to={cta.cta_primary_link} className="btn-gold" data-testid="cta-register">{cta.cta_primary_text} <ArrowRight size={16} /></Link>
              <Link to={cta.cta_secondary_link} className="btn-ghost-dark" data-testid="cta-pricing">{cta.cta_secondary_text}</Link>
            </div>
          </div>
          <div className="card-light p-10">
            <div className="label-tag mb-4" style={{ color: "#000" }}>{cta.newsletter_label}</div>
            <div className="font-heading font-black uppercase text-2xl">{cta.newsletter_title}</div>
            <form onSubmit={subscribe} className="mt-6">
              <input required type="email" placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)}
                className="input-sharp" data-testid="newsletter-email-input" />
              <button className="btn-gold mt-6" data-testid="newsletter-submit-btn">Subscribe <ArrowRight size={16} /></button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function StatBlock({ number, label }) {
  return (
    <div>
      <div className="font-heading font-black text-4xl md:text-5xl metallic-gold">{number}</div>
      <div className="mt-1 text-xs uppercase tracking-widest text-white/60">{label}</div>
    </div>
  );
}

function Feature({ icon, title, text }) {
  return (
    <div className="card-light p-8 hover-lift">
      <div className="text-[color:var(--gold)]">{icon}</div>
      <div className="font-heading uppercase font-bold text-xl mt-4">{title}</div>
      <p className="text-neutral-600 text-sm mt-3 leading-relaxed">{text}</p>
    </div>
  );
}
